import { useState } from 'react';

import { Button } from '@/shared/ui/button';
import { EMPTY_PLACEHOLDER } from '@/shared/format';
import { useAssistantDraftActions } from '@/modules/assistant/hooks/use-assistant-draft';
import { useAssistantDraftStore } from '@/modules/assistant/shared/assistant-store';
import {
  assistantDraftActionsClasses,
  assistantDraftClasses,
  assistantDraftFieldClasses,
  assistantDraftFieldNameClasses,
  assistantDraftFieldValueClasses,
  assistantDraftHeadingClasses,
  assistantDraftReviewClasses,
} from '@/modules/assistant/parts/styles';
import type { AssistantDraft } from '@/api/schemas/assistant';

export type DraftReviewProps = {
  /** The draft the agent produced. */
  draft: AssistantDraft;
};

/**
 * The DRAFT — shown for review, never applied.
 *
 * ---------------------------------------------------------------------------
 * THE PANEL DOES NOT WRITE
 * ---------------------------------------------------------------------------
 * There is no write in this component, and there is no button here that submits
 * anything. "Apply to form" publishes the draft to the module's store; a form
 * page that accepts this capability subscribes and fills ITS OWN state, and the
 * ordinary form performs the ordinary write with the ordinary validation.
 *
 * A model that could write would be a model that could write wrongly, and a
 * person who cannot see the values before they land has no way to object.
 */
export function DraftReview({ draft }: DraftReviewProps) {
  const { setDraft, clearDraft } = useAssistantDraftActions();
  const published = useAssistantDraftStore((state) => state.draft);
  const [discarded, setDiscarded] = useState(false);

  // The store is the truth about whether this draft is waiting for a form. The
  // local flag only records that THIS draft was withdrawn.
  const isPublished = published !== null && published.capability === draft.capability;

  return (
    <section className={assistantDraftClasses} aria-label={`Draft for ${draft.capability}`}>
      <p className={assistantDraftHeadingClasses}>Draft for {draft.capability}</p>

      {/* The server's own review sentence, shown verbatim. */}
      <p className={assistantDraftReviewClasses}>{draft.review}</p>

      <dl>
        {Object.entries(draft.fields).map(([name, value]) => (
          <div key={name} className={assistantDraftFieldClasses}>
            <dt className={assistantDraftFieldNameClasses}>{name}</dt>
            <dd className={assistantDraftFieldValueClasses}>
              {value === '' ? EMPTY_PLACEHOLDER : value}
            </dd>
          </div>
        ))}
      </dl>

      <div className={assistantDraftActionsClasses}>
        {isPublished ? (
          <span className="text-sm text-content-muted">Published for the form.</span>
        ) : (
          <Button size="sm" onClick={() => setDraft(draft)}>
            Apply to form
          </Button>
        )}

        {discarded ? (
          <span className="text-sm text-content-muted">Discarded.</span>
        ) : (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              clearDraft();
              setDiscarded(true);
            }}
          >
            Discard
          </Button>
        )}
      </div>
    </section>
  );
}
